import type { InspectieType } from "./enums";

export const INSPECTIE_TYPE_METADATA: Record<InspectieType, { label: string; suffix: string; rapportSuffix: string }> = {
  scope8pi: { label: "Scope 8 PI", suffix: "8PI", rapportSuffix: "08PI" },
  scope8ebi: { label: "Scope 8 EBI", suffix: "8EBI", rapportSuffix: "08EBI" },
  scope10: { label: "Scope 10", suffix: "10", rapportSuffix: "10" },
  scope12pi: { label: "Scope 12 PI", suffix: "12PI", rapportSuffix: "12PI" },
  scope12ebi: { label: "Scope 12 EBI", suffix: "12EBI", rapportSuffix: "12EBI" },
  nen1010: { label: "NEN 1010", suffix: "NEN1010", rapportSuffix: "1010" },
  nen3140: { label: "NEN 3140", suffix: "NEN3140", rapportSuffix: "3140" },
};

export const inspectieTypeLabels: Record<InspectieType, string> = Object.fromEntries(
  Object.entries(INSPECTIE_TYPE_METADATA).map(([type, metadata]) => [type, metadata.label]),
) as Record<InspectieType, string>;

export const inspectieTypeSuffixes: Record<InspectieType, string> = Object.fromEntries(
  Object.entries(INSPECTIE_TYPE_METADATA).map(([type, metadata]) => [type, metadata.suffix]),
) as Record<InspectieType, string>;

export const rapportNrSuffixes: Record<InspectieType, string> = Object.fromEntries(
  Object.entries(INSPECTIE_TYPE_METADATA).map(([type, metadata]) => [type, metadata.rapportSuffix]),
) as Record<InspectieType, string>;

export const getInspectieTypeLabel = (type: InspectieType): string => inspectieTypeLabels[type];

export const getInspectieTypeSuffix = (type: InspectieType): string => inspectieTypeSuffixes[type];

export const getRapportNrSuffix = (type: InspectieType): string | undefined =>
  INSPECTIE_TYPE_METADATA[type]?.rapportSuffix;

export const formatInspectieDocumentNr = (baseNr: string | null | undefined, type: InspectieType): string => {
  if (!baseNr) return "";
  return `${baseNr}-${getInspectieTypeSuffix(type)}`;
};

/**
 * Suffix a rapportnummer with its inspection-type code (e.g. "INSPR26050008" ->
 * "INSPR26050008-10" for scope10, "-08PI" for scope8pi). Idempotent: a base that
 * already carries the suffix is returned unchanged, and an unknown type leaves
 * the base untouched.
 */
export const formatRapportNr = (baseNr: string | null | undefined, type: InspectieType): string => {
  if (!baseNr) return "";
  const suffix = getRapportNrSuffix(type);
  if (!suffix) return baseNr;
  return baseNr.endsWith(`-${suffix}`) ? baseNr : `${baseNr}-${suffix}`;
};

/**
 * Document number for a herstelrapport (re-inspection report): the rapportnummer
 * with its inspection-type code plus a trailing herinspectie marker. The first
 * round is "-H" (e.g. "INSPR26050008-10-H"), later rounds carry the round number
 * ("-H2", "-H3", …). Idempotent: a base already carrying an "-H"/"-H{n}" suffix
 * is returned unchanged.
 */
export const formatHerstelRapportNr = (
  baseNr: string | null | undefined,
  type: InspectieType,
  round = 1,
): string => {
  const rapportNr = formatRapportNr(baseNr, type);
  if (!rapportNr) return "";
  if (/-H[0-9]*$/.test(rapportNr)) return rapportNr;
  const marker = round > 1 ? `-H${round}` : "-H";
  return `${rapportNr}${marker}`;
};
